<template>
  <div
    class="v-step"
    :style="style"
    :class="[
      !isSimple && `is-${$parent.direction}`,
      isSimple && 'is-simple',
      isLast && !space && !isCenter && 'is-flex',
      isCenter && !isVertical && !isSimple && 'is-center',
      `title-in-${headerAlign}`
     ]">
    <!-- icon & line -->
    <div
      class="v-step__head"
      :class="`is-${currentStatus}`">
      <div
        class="v-step__line"
        :style="stepLineStyle"
      >
        <i class="v-step__line-inner" :style="lineStyle"></i>
      </div>
      <div
        class="v-step__title"
        ref="title"
        v-if="headerAlign === 'top'"
        :class="['is-' + currentStatus]">
        <slot name="title">{{ title }}</slot>
      </div>
      <div class="v-step__icon" :class="`is-${icon ? 'icon' : 'text'}`" :style="iconStyles">
        <slot
          v-if="currentStatus !== 'success' && currentStatus !== 'error'"
          name="icon">
          <i v-if="icon" class="v-step__icon-inner" :class="[icon]"></i>
          <div class="v-step__icon-inner" v-if="!icon && !isSimple">{{ index + 1 }}</div>
        </slot>
        <i
          v-else
          :class="['el-icon-' + (currentStatus === 'success' ? 'check' : 'close')]"
          class="v-step__icon-inner is-status"
        >
        </i>
      </div>
    </div>
    <!-- title & description -->
    <div class="v-step__main">
      <div
        class="v-step__title"
        ref="title"
        v-if="headerAlign !== 'top'"
        :class="['is-' + currentStatus]">
        <slot name="title">{{ title }}</slot>
      </div>
      <div v-if="isSimple" class="v-step__arrow"></div>
      <div
        v-else
        class="v-step__description"
        :class="['is-' + currentStatus]">
        <slot name="description">{{ description }}</slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'v-step',
  props: {
    title: String,
    icon: String,
    description: String,
    status: String
  },
  data() {
    return {
      index: -1,
      lineStyle: {},
      internalStatus: ''
    };
  },
  beforeCreate() {
    this.$parent.steps.push(this);
  },
  beforeDestroy() {
    const steps = this.$parent.steps;
    const index = steps.indexOf(this);
    if (index >= 0) {
      steps.splice(index, 1);
    }
  },
  computed: {
    currentStatus() {
      return this.status || this.internalStatus;
    },
    prevStatus() {
      const prevStep = this.$parent.steps[this.index - 1];
      return prevStep ? prevStep.currentStatus : 'wait';
    },
    isCenter() {
      return this.$parent.alignCenter;
    },
    isVertical() {
      return this.$parent.direction === 'vertical';
    },
    isSimple() {
      return this.$parent.simple;
    },
    isLast() {
      const parent = this.$parent;
      return parent.steps[parent.steps.length - 1] === this;
    },
    headerAlign () {
        return this.$parent.headerAlign
    },
    stepsCount() {
      return this.$parent.steps.length;
    },
    space() {
      const { isSimple, $parent: { space } } = this;
      return isSimple ? '' : space ;
    },
    style: function() {
      const style = {};
      const parent = this.$parent;
      const len = parent.steps.length;
      const space = (typeof this.space === 'number'
        ? this.space + 'px'
        : this.space
          ? this.space
          : 100 / (len - (this.isCenter ? 0 : 1)) + '%');
      style.flexBasis = space;
      if (this.isVertical) return style;
      if (this.isLast) {
        style.maxWidth = 100 / this.stepsCount + '%';
      } else {
        style.marginRight = -this.$parent.stepOffset + 'px';
      }
      return style;
    },
    iconStyles () {
        const size = this.$parent.size
        return {
            width: `${size}px`,
            height: `${size}px`,
            fontSize: `${this.$parent.iconSize}px`
        }
    },
    stepLineStyle () {
        const size = this.$parent.size
        const lineSpace = this.$parent.lineSpace
        const styles = {
            top: this.$parent.headerAlign === 'top' ? `calc(38px + ${size / 2}px - 2px)` : `calc(50% - 2px)`
        }
        if (this.isLast) styles.marginRight = this.$parent.stepOffset + 'px'
        if (!this.isCenter) return styles
        const value = size / 2 + lineSpace
        return {
            left: `calc(50% + ${value}px)`,
            right: `calc(-50% + ${value}px)`,
            ...styles
        }
    }
  },
  methods: {
    updateStatus(val) {
      const prevChild = this.$parent.$children[this.index - 1];
      if (val > this.index) {
        this.internalStatus = this.$parent.finishStatus;
      } else if (val === this.index && this.prevStatus !== 'error') {
        this.internalStatus = this.$parent.processStatus;
      } else {
        this.internalStatus = 'wait';
      }
      if (prevChild) prevChild.calcProgress(this.internalStatus);
    },
    calcProgress(status) {
      let step = 100;
      const style = {};
      style.transitionDelay = 150 * this.index + 'ms';
      if (status === this.$parent.processStatus) {
        step = this.currentStatus !== 'error' ? 0 : 0;
      } else if (status === 'wait') {
        step = 0;
        style.transitionDelay = (-150 * this.index) + 'ms';
      }
      style.borderWidth = step && !this.isSimple ? '1px' : 0;
      this.$parent.direction === 'vertical'
        ? style.height = step + '%'
        : style.width = step + '%';
      this.lineStyle = style;
    }
  },
  mounted() {
    const unwatch = this.$watch('index', val => {
      this.$watch('$parent.active', this.updateStatus, { immediate: true });
      this.$watch('$parent.processStatus', () => {
        const activeIndex = this.$parent.active;
        this.updateStatus(activeIndex);
      }, { immediate: true });
      unwatch();
    });
  }
};
</script>

<style lang="less" scoped>
@import '../color.less';
.v-step {
    position: relative;
    flex-shrink: 1
}

.v-step:last-of-type .v-step__line {
    display: none
}

.v-step:last-of-type.is-flex {
    flex-basis: auto!important;
    flex-shrink: 0;
    flex-grow: 0
}

.v-step:last-of-type .v-step__description,.v-step:last-of-type .v-step__main {
    padding-right: 0
}

.v-step__head {
    position: relative;
    width: 100%
}

.v-step__head.is-process {
    color: #979797;
    border-color: #979797;
}

.v-step__head.is-wait {
    color: #c0c4cc;
    border-color: #c0c4cc
}

.v-step__head.is-success {
    color: @primary;
    border-color: @primary;
}

.v-step__head.is-error {
    color: #f56c6c;
    border-color: #f56c6c
}

.v-step__head.is-finish {
    color: @primary;
    border-color: @primary;
}

.v-step__icon {
    position: relative;
    z-index: 1;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    font-size: 14px;
    box-sizing: border-box;
    background: #fff;
    transition: .15s ease-out
}

.v-step__icon.is-text {
    border-radius: 50%;
    border: 2px solid;
    border-color: inherit
}

.v-step__icon.is-icon {
    width: 40px
}

.v-step__icon-inner {
    display: inline-block;
    user-select: none;
    text-align: center;
    font-weight: 700;
    line-height: 1;
    color: inherit
}

.v-step__icon-inner[class*=v-icon]:not(.is-status) {
    font-size: 25px;
    font-weight: 400
}

.v-step__icon-inner.is-status {
    transform: translateY(1px)
}

.v-step__line {
    position: absolute;
    border-color: inherit;
    background-color: #c0c4cc
}

.v-step__line-inner {
    display: block;
    border: 1px solid;
    border-color: inherit;
    transition: .15s ease-out;
    box-sizing: border-box;
    width: 0;
    height: 0
}

.v-step__main {
    white-space: normal;
    text-align: left
}

.v-step__title {
    font-size: 16px;
    line-height: 38px;
    height: 38px;
}

.v-step__title.is-process {
    font-weight: 700;
    color: #979797;
}

.v-step__title.is-wait {
    color: #c0c4cc
}

.v-step__title.is-success {
    color: @primary
}

.v-step__title.is-error {
    color: #f56c6c
}

.v-step__title.is-finish {
    color: @primary
}

.v-step__description {
    padding-right: 10%;
    margin-top: -5px;
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
    word-break: break-all;
}

.v-step.title-in-top .v-step__description {
    margin-top: 4px;
}

.v-step__description.is-process {
    color: #979797;
}

.v-step__description.is-wait {
    color: #c0c4cc
}

.v-step__description.is-success {
    color: @primary
}

.v-step__description.is-error {
    color: #f56c6c
}

.v-step__description.is-finish {
    color: @primary
}

.v-step.is-horizontal {
    display: inline-block
}

.v-step.is-horizontal .v-step__line {
    height: 2px;
    top: calc(50% - 2px);
    left: 0;
    right: 0
}

.v-step.is-vertical {
    display: flex
}

.v-step.is-vertical .v-step__head {
    flex-grow: 0;
    width: 24px
}

.v-step.is-vertical .v-step__main {
    padding-left: 10px;
    flex-grow: 1
}

.v-step.is-vertical .v-step__title {
    line-height: 24px;
    padding-bottom: 8px
}

.v-step.is-vertical .v-step__line {
    width: 2px;
    top: 0;
    bottom: 0;
    left: 11px
}

.v-step.is-vertical .v-step__icon.is-icon {
    width: 24px
}

.v-step.is-center .v-step__head,.v-step.is-center .v-step__main {
    text-align: center
}

.v-step.is-center .v-step__description {
    padding-left: 20%;
    padding-right: 20%
}

.v-step.is-center .v-step__line {
    left: 50%;
    right: -50%
}

.v-step.is-simple {
    display: flex;
    align-items: center
}

.v-step.is-simple .v-step__head {
    width: auto;
    font-size: 0;
    padding-right: 10px
}

.v-step.is-simple .v-step__icon {
    background: transparent;
    width: 16px;
    height: 16px;
    font-size: 12px
}

.v-step.is-simple .v-step__icon-inner[class*=v-icon]:not(.is-status) {
    font-size: 18px
}

.v-step.is-simple .v-step__icon-inner.is-status {
    transform: scale(.8) translateY(1px)
}

.v-step.is-simple .v-step__main {
    position: relative;
    display: flex;
    align-items: stretch;
    flex-grow: 1
}

.v-step.is-simple .v-step__title {
    font-size: 16px;
    line-height: 20px
}

.v-step.is-simple:not(:last-of-type) .v-step__title {
    max-width: 50%;
    word-break: break-all
}

.v-step.is-simple .v-step__arrow {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center
}

.v-step.is-simple .v-step__arrow:after,.v-step.is-simple .v-step__arrow:before {
    content: "";
    display: inline-block;
    position: absolute;
    height: 15px;
    width: 1px;
    background: #c0c4cc
}

.v-step.is-simple .v-step__arrow:before {
    transform: rotate(-45deg) translateY(-4px);
    transform-origin: 0 0
}

.v-step.is-simple .v-step__arrow:after {
    transform: rotate(45deg) translateY(4px);
    transform-origin: 100% 100%
}

.v-step.is-simple:last-of-type .v-step__arrow {
    display: none
}
</style>


// WEBPACK FOOTER //
// src/components/framework/steps/step.vue